import { Star, Quote, BadgeCheck, Heart } from "lucide-react";
import { TESTIMONIALS, REVIEW_RATING, GOOGLE_REVIEW } from "@/data/business";
import GoogleReviewsLive from "@/components/site/GoogleReviewsLive";

export default function Testimonials() {
  return (
    <section id="reviews" className="bg-slate-50 py-20 lg:py-28 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-600">
            Trusted by Local Homeowners
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900">
            What Our Customers Say
          </h2>

          {/* Trustpilot rating summary */}
          <div className="mt-6 inline-flex flex-col items-center gap-2 bg-white border border-slate-200 rounded-sm px-6 py-4">
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="grid place-items-center h-6 w-6 bg-[#00b67a]">
                  <Star className="h-4 w-4 fill-white text-white" />
                </span>
              ))}
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-bold text-slate-900">{REVIEW_RATING} / 5</span> rated by
              verified customers on{" "}
              <span className="font-bold text-[#00b67a]">Trustpilot</span>
            </p>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="bg-white border border-slate-200 rounded-sm p-7 flex flex-col hover:border-red-600 hover:-translate-y-1 transition-all"
              data-testid={`testimonial-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <Quote className="h-8 w-8 text-red-600/30" />
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#00b67a]">
                  <BadgeCheck className="h-3.5 w-3.5" /> Trustpilot
                </span>
              </div>
              <div className="flex items-center gap-0.5 mt-3">
                {[...Array(t.rating)].map((_, i) => (
                  <span key={i} className="grid place-items-center h-5 w-5 bg-[#00b67a]">
                    <Star className="h-3 w-3 fill-white text-white" />
                  </span>
                ))}
              </div>
              <blockquote className="mt-4 text-slate-700 leading-relaxed flex-1">
                "{t.text}"
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-slate-100">
                <p className="font-heading font-bold text-slate-900">{t.name}</p>
                <p className="text-sm text-slate-500">{t.location}</p>
              </figcaption>
            </figure>
          ))}
        </div>

        <GoogleReviewsLive />

        {/* Small-business review ask */}
        <div className="mt-12 max-w-2xl mx-auto text-center bg-white border border-slate-200 rounded-sm p-8 sm:p-10" data-testid="review-ask-card">
          <span className="grid place-items-center h-12 w-12 mx-auto rounded-full bg-red-600/10">
            <Heart className="h-6 w-6 fill-red-500 text-red-500" />
          </span>
          <p className="mt-5 font-heading text-xl sm:text-2xl font-black tracking-tight text-slate-900">
            We're a locally owned small business — your review means the world to us 🙏
          </p>
          <p className="mt-3 text-slate-600 leading-relaxed">
            A quick 30-second Google review helps your neighbors find trusted garage door help and
            keeps our family business growing. We'd be truly grateful.
          </p>
          <a
            href={GOOGLE_REVIEW.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 bg-[#4285F4] hover:bg-[#3367d6] active:scale-[0.98] text-white font-bold px-8 py-3.5 rounded-sm transition-all shadow-sm"
            data-testid="google-review-link"
          >
            <Star className="h-4 w-4 fill-white" /> Leave Us a Google Review
          </a>
          <div className="mt-4">
            <a
              href="https://www.trustpilot.com/review/prosperdoors.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#00b67a] transition-colors"
              data-testid="trustpilot-link"
            >
              <BadgeCheck className="h-4 w-4" /> Read our reviews on Trustpilot
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
